// src/components/DDTIngressoTab.js
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Plus, Trash2, X, Upload, FileText, Image, Eye, Download } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";

const STATI = {
  ricevuto:   { label: "Ricevuto",   bg: "#dbeafe", col: "#1d4ed8" },
  verificato: { label: "Verificato", bg: "#d1fae5", col: "#065f46" },
  contestato: { label: "Contestato", bg: "#fee2e2", col: "#991b1b" },
};

function DDTIngressoForm({ ddt, projectId, projectName, codiceCommessa, orgId, onClose, commessaChiusa=false }) {
  const { currentUser } = useAuth();
  const { addToast } = useToast();
  const fileRef = useRef();

  const oggi = toLocalDateStr(new Date());
  const [form, setForm] = useState(ddt || {
    dataRicezione: oggi,
    mittente: "",
    nDocumento: "",
    descrizione: "",
    note: "",
    stato: "ricevuto",
  });
  const [allegati, setAllegati] = useState(ddt?.allegati || []);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(ddt?.allegati?.[0] || null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const estensione = file.name.split(".").pop().toLowerCase();
      const nomeFile = `ddt-ingresso/${orgId}/${projectId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, nomeFile);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const tipo = ["jpg","jpeg","png","webp","heic"].includes(estensione) ? "foto" : "pdf";
      setAllegati(a => [...a, { url, nome: file.name, tipo, path: nomeFile }]);
      addToast(`${tipo === "foto" ? "Foto" : "PDF"} caricato ✅`, "success");
    } catch (err) {
      addToast("Errore nel caricamento", "error");
    }
    setUploading(false);
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await uploadFile(file);
    }
    e.target.value = "";
  };

  const removeAllegato = (i) => setAllegati(a => a.filter((_, j) => j !== i));

  const salva = async () => {
    if (commessaChiusa) return;
    if (!form.mittente.trim()) { addToast("Inserisci il mittente", "error"); return; }
    setSaving(true);
    const dati = {
      ...form, allegati, projectId, projectName, codiceCommessa: codiceCommessa || "",
      orgId, compilatoreUid: currentUser?.uid, compilatoreNome: currentUser?.displayName || "",
      updatedAt: serverTimestamp(),
    };
    if (ddt?.id) {
      await updateDoc(doc(db, "ddtIngresso", ddt.id), dati);
      addToast("DDT aggiornato ✅", "success");
    } else {
      await addDoc(collection(db, "ddtIngresso"), { ...dati, createdAt: serverTimestamp() });
      addToast("DDT in ingresso salvato ✅", "success");
    }
    setSaving(false);
    onClose();
  };

  const inp = { padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 7, fontSize: 13, fontFamily: "'Inter',sans-serif", outline: "none", width: "100%", boxSizing: "border-box" };
  const lbl = { fontSize: 11, fontWeight: 600, color: "#6b7280", display: "block", marginBottom: 4 };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e5e7eb", padding: "12px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#1e1b4b" }}>
            {ddt ? "Modifica DDT in ingresso" : "Nuovo DDT in ingresso"}
          </div>
          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
            {codiceCommessa && `[${codiceCommessa}] `}{projectName}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems:"center" }}>
          {commessaChiusa && <span style={{ fontSize:11, fontWeight:700, color:"#dc2626" }}>🔒 Commessa chiusa</span>}
          {!commessaChiusa && (
            <button onClick={salva} disabled={saving || uploading}
              style={{ padding: "7px 18px", border: "none", borderRadius: 8, background: "#059669", color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>
              {saving ? "..." : "💾 Salva"}
            </button>
          )}
          <button onClick={onClose} style={{ padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff", cursor: "pointer" }}>
            <X size={14} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
        {/* Dati documento */}
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
          <div style={{ background: "#f9fafb", padding: "10px 16px", borderBottom: "1px solid #e5e7eb" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1e1b4b" }}>📄 Dati documento</span>
          </div>
          <div style={{ padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Data ricezione *</label>
              <input type="date" style={inp} value={form.dataRicezione} onChange={e => upd("dataRicezione", e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Mittente / Fornitore *</label>
              <input style={inp} value={form.mittente} onChange={e => upd("mittente", e.target.value)} placeholder="Es. Schneider Electric, DHL..." />
            </div>
            <div>
              <label style={lbl}>N° documento DDT</label>
              <input style={inp} value={form.nDocumento} onChange={e => upd("nDocumento", e.target.value)} placeholder="Numero sul documento cartaceo" />
            </div>
          </div>
          <div style={{ padding: "0 16px 16px", display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Descrizione materiale ricevuto *</label>
              <textarea value={form.descrizione} onChange={e => upd("descrizione", e.target.value)}
                placeholder="Es. N.3 quadri elettrici serie iC60, N.10 interruttori magnetotermici..."
                style={{ ...inp, minHeight: 80, resize: "vertical" }} />
            </div>
            <div>
              <div style={{ marginBottom: 12 }}>
                <label style={lbl}>Stato</label>
                <select style={inp} value={form.stato} onChange={e => upd("stato", e.target.value)}>
                  {Object.entries(STATI).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Note</label>
                <textarea value={form.note} onChange={e => upd("note", e.target.value)}
                  placeholder="Eventuali difformità, danni, mancanze..."
                  style={{ ...inp, minHeight: 52, resize: "vertical" }} />
              </div>
            </div>
          </div>
        </div>

        {/* Upload allegati */}
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
          <div style={{ background: "#fffbeb", padding: "10px 16px", borderBottom: "1px solid #fde68a" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#d97706" }}>📎 Allegati — Foto e PDF del DDT</span>
          </div>
          <div style={{ padding: 16 }}>
            {/* Area upload */}
            <div
              onClick={() => fileRef.current?.click()}
              style={{ border: "2px dashed #d97706", borderRadius: 10, padding: "24px 20px", textAlign: "center", cursor: "pointer", background: "#fffbeb", marginBottom: allegati.length > 0 ? 14 : 0 }}>
              <Upload size={28} color="#d97706" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 700, color: "#d97706", marginBottom: 4 }}>
                {uploading ? "Caricamento in corso..." : "Carica foto o PDF"}
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                Fotografia del DDT firmato · PDF · JPG, PNG, HEIC, PDF
              </div>
              <div style={{ fontSize: 11, color: "#b45309", marginTop: 4, fontWeight: 600 }}>
                Puoi selezionare più file contemporaneamente
              </div>
            </div>
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.heic"
              style={{ display: "none" }} onChange={handleFileChange} />

            {/* Lista allegati (esclude quello attualmente in anteprima sotto) */}
            {allegati.filter(a => a !== preview).length > 0 && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
                {allegati.map((a, i) => a === preview ? null : (
                  <div key={i} style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#fff" }}>
                    {a.tipo === "foto" ? (
                      <img src={a.url} alt={a.nome}
                        style={{ width: "100%", height: 100, objectFit: "cover", cursor: "pointer" }}
                        onClick={() => setPreview(a)} />
                    ) : (
                      <div style={{ height: 100, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "#fef2f2", cursor: "pointer" }}
                        onClick={() => setPreview(a)}>
                        <FileText size={32} color="#dc2626" />
                        <div style={{ fontSize: 10, color: "#dc2626", marginTop: 4, fontWeight: 600 }}>PDF</div>
                      </div>
                    )}
                    <div style={{ padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 10, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{a.nome}</span>
                      <button onClick={() => removeAllegato(i)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5", padding: "0 0 0 4px", flexShrink: 0 }}>
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Anteprima inline dell'allegato selezionato */}
            {preview && (
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", marginTop: 14 }}>
                <div style={{ background: "#fffbeb", padding: "8px 12px", borderBottom: "1px solid #fde68a",
                  display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#d97706", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {preview.tipo === "foto" ? "🖼️" : "📄"} {preview.nome}
                  </span>
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    <a href={preview.url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 11, color: "#7c3aed", textDecoration: "none", fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 4 }}>
                      <Eye size={12} /> Apri
                    </a>
                    <a href={preview.url} download={preview.nome}
                      style={{ fontSize: 11, color: "#059669", textDecoration: "none", fontWeight: 600,
                        display: "flex", alignItems: "center", gap: 4 }}>
                      <Download size={12} /> Scarica
                    </a>
                    <button onClick={() => setPreview(null)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 0, display: "flex", alignItems: "center" }}>
                      <X size={14} />
                    </button>
                  </div>
                </div>
                {preview.tipo === "foto" ? (
                  <img src={preview.url} alt={preview.nome}
                    style={{ width: "100%", maxHeight: 400, objectFit: "contain", background: "#111827" }} />
                ) : (
                  <iframe src={preview.url} style={{ width: "100%", height: 350, border: "none" }}
                    title={preview.nome} />
                )}
              </div>
            )}
          </div>
        </div>

        {allegati.length === 0 && (
          <div style={{ background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 14px", fontSize: 11, color: "#92400e" }}>
            ⚠️ Nessun allegato caricato. Si consiglia di fotografare sempre il DDT firmato per avere prova della consegna.
          </div>
        )}
      </div>
    </div>
  );
}

export default function DDTIngressoTab({ projectId, projectName, codiceCommessa, orgId, commessaChiusa=false }) {
  const { addToast } = useToast();
  const [lista, setLista] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editDDT, setEditDDT] = useState(null);

  useEffect(() => {
    if (!projectId || !orgId) return;
    return onSnapshot(
      query(collection(db, "ddtIngresso"), where("projectId", "==", projectId), where("orgId", "==", orgId)),
      snap => setLista(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => b.dataRicezione?.localeCompare(a.dataRicezione || "") || 0))
    );
  }, [projectId, orgId]);

  const elimina = async id => {
    if (commessaChiusa) return;
    if (!await confirm("Eliminare questo DDT in ingresso?", { type: "danger" })) return;
    await deleteDoc(doc(db, "ddtIngresso", id));
    addToast("Eliminato", "info");
  };

  if (showForm || editDDT) {
    return <DDTIngressoForm ddt={editDDT} projectId={projectId} projectName={projectName}
      codiceCommessa={codiceCommessa} orgId={orgId} commessaChiusa={commessaChiusa}
      onClose={() => { setShowForm(false); setEditDDT(null); }} />;
  }

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1e1b4b", margin: 0, display: "flex", alignItems: "center", gap: 6 }}>
          📥 DDT in ingresso
        </h3>
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{lista.length} documento/i</span>
        {!commessaChiusa ? (
          <button onClick={() => setShowForm(true)} className="btn btn-primary"
            style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 12px", fontSize: 12 }}>
            <Plus size={13} /> Nuovo DDT ricevuto
          </button>
        ) : (
          <span style={{ fontSize: 11, fontWeight: 700, color: "#dc2626" }}>🔒 Commessa chiusa — sola consultazione</span>
        )}
      </div>

      <div style={{ background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "8px 14px", marginBottom: 16, fontSize: 11, color: "#0369a1" }}>
        💡 Archivia qui i DDT firmati ricevuti dai corrieri e fornitori. Carica la foto del documento cartaceo o il PDF allegato alla email.
      </div>

      {lista.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>
          <div style={{ fontSize: 48, marginBottom: 12, opacity: .15 }}>📥</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Nessun DDT in ingresso</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Registra il primo DDT ricevuto in cantiere</div>
        </div>
      ) : (
        <div>
          {lista.map(d => {
            const stato = STATI[d.stato] || STATI.ricevuto;
            return (
              <div key={d.id} style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "12px 16px", marginBottom: 10, background: "#fff", display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}
                onClick={() => setEditDDT(d)}>
                {/* Anteprima prima foto */}
                {d.allegati?.[0]?.tipo === "foto" ? (
                  <img src={d.allegati[0].url} alt="DDT"
                    style={{ width: 60, height: 60, objectFit: "cover", borderRadius: 6, flexShrink: 0, border: "1px solid #e5e7eb" }} />
                ) : d.allegati?.[0]?.tipo === "pdf" ? (
                  <div style={{ width: 60, height: 60, borderRadius: 6, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "1px solid #fca5a5" }}>
                    <FileText size={24} color="#dc2626" />
                  </div>
                ) : (
                  <div style={{ width: 60, height: 60, borderRadius: 6, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <Image size={24} color="#9ca3af" />
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#1e1b4b" }}>{d.mittente}</span>
                    {d.nDocumento && <span style={{ fontSize: 11, fontFamily: "monospace", color: "#7c3aed" }}>#{d.nDocumento}</span>}
                    <span style={{ fontSize: 10, padding: "1px 7px", borderRadius: 10, background: stato.bg, color: stato.col, fontWeight: 700 }}>{stato.label}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#374151", marginBottom: 3 }}>{d.descrizione}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>
                    {d.dataRicezione ? new Date(d.dataRicezione + "T00:00:00").toLocaleDateString("it-IT") : "—"}
                    {d.compilatoreNome && ` · ${d.compilatoreNome}`}
                    {d.allegati?.length > 0 && (
                      <span style={{ marginLeft: 8, color: "#d97706", fontWeight: 600 }}>
                        📎 {d.allegati.length} allegato/i
                      </span>
                    )}
                  </div>
                  {d.note && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 2, fontStyle: "italic" }}>⚠️ {d.note}</div>}
                </div>
                {!commessaChiusa && (
                  <button onClick={e => { e.stopPropagation(); elimina(d.id); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#fca5a5", padding: 4, flexShrink: 0 }}>
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
